import React, { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { ArrowDown, CheckCircle2 } from 'lucide-react';

export default function Distribuidor() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    nome: '',
    contacto: '',
    email: '',
    localidade: '',
    disponibilidade: '',
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.disponibilidade) {
      toast({ title: 'Escolha a disponibilidade horária', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/distribuidor`, form);
      setSent(true);
      toast({
        title: 'Inscrição enviada',
        description: 'Recebemos a sua candidatura. Entraremos em contacto brevemente.',
      });
    } catch (err) {
      toast({
        title: 'Não foi possível enviar',
        description: 'Tente novamente em alguns minutos ou escreva para squaredartsbr@gmail.com.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="distribuidor" className="relative w-full py-24 md:py-32 bg-[color:var(--sd-bg)]">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="sd-panel sd-brackets p-8 md:p-12">
          <div className="text-[10px] tracking-[0.35em] text-[color:var(--sd-cyan)]/70 font-mono mb-3">
            // FORMULÁRIO_DISTRIBUIDOR.EXE
          </div>
          <h3 className="font-display font-black text-[color:var(--sd-cyan)] text-2xl md:text-4xl tracking-widest sd-glow">
            QUER SER UM DISTRIBUIDOR?
          </h3>
          <p className="mt-6 font-mono text-[color:var(--sd-text)]/85 leading-relaxed">
            Seja o rosto da Square Darts Brasil na sua região. Oferecemos
            suporte completo para que você possa representar nossa tecnologia
            e expandir o desporto dos dardos.
          </p>

          {sent ? (
            <div className="mt-10 p-6 border border-[color:var(--sd-cyan)]/40 bg-[rgba(34,230,230,0.05)] flex items-center gap-4">
              <CheckCircle2 className="text-[color:var(--sd-cyan)]" />
              <div>
                <div className="font-display text-[color:var(--sd-cyan)] tracking-widest">
                  INSCRIÇÃO RECEBIDA
                </div>
                <div className="font-mono text-sm text-[color:var(--sd-text)]/80 mt-1">
                  Iremos entrar em contacto assim que a fase de pré-lançamento estiver ativa.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 font-mono">
              <div>
                <Label className="text-[color:var(--sd-cyan)] tracking-[0.2em] text-xs">Nome*</Label>
                <Input
                  required
                  value={form.nome}
                  data-testid="distribuidor-nome-input"
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-2 bg-transparent border-[rgba(34,230,230,0.3)] focus:border-[color:var(--sd-cyan)] text-white rounded-none"
                />
              </div>
              <div>
                <Label className="text-[color:var(--sd-cyan)] tracking-[0.2em] text-xs">Contacto*</Label>
                <Input
                  required
                  value={form.contacto}
                  data-testid="distribuidor-contacto-input"
                  onChange={(e) => setForm({ ...form, contacto: e.target.value })}
                  className="mt-2 bg-transparent border-[rgba(34,230,230,0.3)] focus:border-[color:var(--sd-cyan)] text-white rounded-none"
                />
              </div>
              <div>
                <Label className="text-[color:var(--sd-cyan)] tracking-[0.2em] text-xs">Email*</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  data-testid="distribuidor-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 bg-transparent border-[rgba(34,230,230,0.3)] focus:border-[color:var(--sd-cyan)] text-white rounded-none"
                />
              </div>
              <div>
                <Label className="text-[color:var(--sd-cyan)] tracking-[0.2em] text-xs">Localidade*</Label>
                <Input
                  required
                  value={form.localidade}
                  data-testid="distribuidor-localidade-input"
                  onChange={(e) => setForm({ ...form, localidade: e.target.value })}
                  className="mt-2 bg-transparent border-[rgba(34,230,230,0.3)] focus:border-[color:var(--sd-cyan)] text-white rounded-none"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[color:var(--sd-cyan)] tracking-[0.2em] text-xs">Disponibilidade horária*</Label>
                <Select
                  value={form.disponibilidade}
                  onValueChange={(v) => setForm({ ...form, disponibilidade: v })}
                  required
                >
                  <SelectTrigger data-testid="distribuidor-disponibilidade-select" className="mt-2 bg-transparent border-[rgba(34,230,230,0.3)] focus:border-[color:var(--sd-cyan)] text-white rounded-none">
                    <SelectValue placeholder="Escolha um horário" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0d10] border-[rgba(34,230,230,0.3)] text-white">
                    <SelectItem value="manha">Manhã (08h–12h)</SelectItem>
                    <SelectItem value="tarde">Tarde (12h–18h)</SelectItem>
                    <SelectItem value="noite">Noite (18h–22h)</SelectItem>
                    <SelectItem value="flexivel">Horário flexível</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 mt-2">
                <button type="submit" disabled={sending} data-testid="distribuidor-submit-btn" className="sd-btn w-full md:w-auto disabled:opacity-60">
                  {sending ? 'A ENVIAR...' : 'FAZER A INSCRIÇÃO'} <ArrowDown size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
